import React, { useEffect } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { UserButton, useAuth } from '@clerk/clerk-react';
import { Home, Users, Mail, FileText, Video, Settings, LogOut, Menu, X, MapPin, Target } from 'lucide-react';
import TopNav from '../components/TopNav';

interface SidebarLinkProps {
  to: string;
  icon: React.ElementType;
  children: React.ReactNode;
  onClick?: () => void; // For mobile menu close
}

const SidebarLink: React.FC<SidebarLinkProps> = ({ to, icon: Icon, children, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className="flex items-center px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-200 hover:text-gray-900 rounded-lg transition-colors duration-150 group"
  >
    <Icon className="h-5 w-5 mr-3 text-gray-500 group-hover:text-gray-700 transition-colors duration-150" />
    {children}
  </Link>
);

const DashboardLayout: React.FC = () => {
  const { getToken, signOut } = useAuth();

  // TEMPORARY: Log JWT to console
  useEffect(() => {
    const fetchTokenAndLog = async () => {
      if (typeof getToken === 'function') { // Check if getToken is available
        try {
          const token = await getToken();
          console.log('Clerk JWT from DashboardLayout:', token);
        } catch (e) {
          console.error('Error getting token in DashboardLayout', e);
        }
      }
    };
    fetchTokenAndLog();
  }, [getToken]);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { to: "/", icon: Home, label: "Dashboard" },
    { to: "/guests", icon: Users, label: "Guests" },
    { to: "/target-guests", icon: Target, label: "Target Guests" },
    { to: "/nominations", icon: Mail, label: "Nominations" },
    { to: "/applications", icon: FileText, label: "Applications" },
    { to: "/episodes", icon: Video, label: "Episodes" },
    { to: "/map", icon: MapPin, label: "Map" },
  ];

  const renderSidebarContent = () => (
    <>
      <div className="px-4 pt-2 pb-4 border-b border-gray-200">
        <Link to="/" onClick={closeMobileMenu} className="flex items-center">
          {/* You can replace this with a logo component if you have one */}
          <span className="text-2xl font-bold text-neutral-800 tracking-tight">Admin Portal</span>
        </Link>
      </div>
      <nav className="flex-grow p-4 space-y-1.5">
        {navLinks.map((link) => (
          <SidebarLink key={link.to} to={link.to} icon={link.icon} onClick={closeMobileMenu}>
            {link.label}
          </SidebarLink>
        ))}
      </nav>
      <div className="p-4 border-t border-gray-200 space-y-2">
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-gray-50">
      <TopNav />
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:flex-col md:w-64 bg-white border-r border-gray-200 pt-16">
        {renderSidebarContent()}
      </aside>

      {/* Mobile Menu Button */}
      <div className="md:hidden fixed top-4 left-4 z-40 mt-16">
        <button
          onClick={toggleMobileMenu}
          className="p-2 text-gray-600 bg-white rounded-md shadow-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
          aria-label="Open sidebar"
        >
          {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Sidebar (Overlay) */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-30 flex pt-16">
          {/* Overlay */}
          <div className="fixed inset-0 bg-black/30" onClick={toggleMobileMenu}></div>
          {/* Sidebar */}
          <aside className="relative flex flex-col w-64 bg-white border-r border-gray-200 shadow-xl">
            {renderSidebarContent()}
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto p-4 md:p-8 pt-20 md:pt-24">
        <Outlet />
      </main>
    </div>
  );
};

export default DashboardLayout;
